import { useCallback, useEffect, useRef, useState } from 'react'
import { Aborted } from '../api/client'
import type { PageOf } from '../api/client'
import { deuteFehler } from './queryError'

/**
 * Wie lange nach der letzten Aenderung gewartet wird, bevor abgefragt wird.
 *
 * Dieselbe Pause wie in `useListQuery` und aus demselben Grund: Ohne sie
 * loest jeder Buchstabe eine Abfrage aus — "NASDAQ" waeren sechs, von denen
 * fuenf verworfen werden.
 */
const TYPING_PAUSE = 300

export interface EndlessList<T> {
  /** Alle bisher geladenen Zeilen, in der Reihenfolge des Servers. */
  rows: T[]
  /** Die ERSTE Ladung laeuft. Dann steht noch nichts da. */
  loading: boolean
  /** Eine WEITERE Seite laeuft. Was da ist, bleibt sichtbar. */
  loadingMore: boolean
  error: string | null
  denied: boolean
  /** Ob der Server noch etwas hat. */
  hasMore: boolean
  /** Naechste Seite anfordern. Mehrfachaufrufe sind unschaedlich. */
  loadMore: () => void
  /** Gesamtzahl, wenn der Server sie gezaehlt hat. */
  total: number | null
  /** Von vorn laden — nach einem Schreiben, das eine Zeile veraendert hat. */
  reload: () => void
}

/**
 * Eine Liste, die beim Scrollen weiterwaechst.
 *
 * WAS SIE VON `useListQuery` UNTERSCHEIDET, ist ein einziger Satz: Eine
 * weitere Seite wird ANGEHAENGT statt zu ersetzen. Alles andere — Tipppause,
 * Abbruch der vorigen Abfrage, Deutung der Fehler — ist dasselbe und kommt
 * deshalb aus derselben Quelle (`queryError`).
 *
 * `params` ENTHAELT KEINE SEITE. Die verwaltet der Haken selbst, und das ist
 * der Kern: Aendert sich irgendetwas an den Parametern — Suche, Filter,
 * Sortierung —, faengt die Liste bei Seite 1 an und wirft das Gesammelte weg.
 * Stuende die Seite mit in `params`, waere jedes Nachladen von einem
 * Filterwechsel nicht zu unterscheiden.
 *
 * KEIN `topic`, anders als bei `useListQuery`. Eine live nachladende Liste
 * muesste bei jedem Hinweis entscheiden, was mit den angehaengten Seiten
 * geschieht — und neue Zeilen am Anfang verschoeben alle Offsets, sodass
 * dieselbe Zeile beim naechsten Nachladen ein zweites Mal kaeme. Solange das
 * nicht geklaert ist, gibt es das hier nicht.
 */
export function useEndlessList<P extends object, T>(
  fetcher: (params: P & { page: number }, signal: AbortSignal)
    => Promise<PageOf<T>>,
  params: P,
  enabled: boolean = true,
): EndlessList<T> {
  const [rows, setRows] = useState<T[]>([])
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(enabled)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [denied, setDenied] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [total, setTotal] = useState<number | null>(null)
  const first = useRef(true)
  // Ein Zaehler als zusaetzlicher Ausloeser fuer `reload`: Die Parameter
  // aendern sich dabei nicht, geladen werden soll trotzdem — nach einem
  // Schreiben, das eine Zeile veraendert hat.
  const [tick, setTick] = useState(0)

  const key = JSON.stringify(params)

  // Parameter geaendert heisst: von vorn. `key` als Abhaengigkeit und nicht
  // `params` — ein Objekt wechselt bei jedem Rendern die Identitaet, sein
  // Inhalt nicht.
  useEffect(() => {
    setPage(1)
    setRows([])
    setHasMore(false)
  }, [key])

  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()

    // Beim ersten Mal ohne Pause: Da hat niemand getippt, und 300 ms waeren
    // beim Oeffnen der Seite sichtbar. Beim NACHLADEN ebenfalls ohne — dort
    // hat niemand getippt, sondern gescrollt, und die Pause waere eine
    // Verzoegerung ohne Zweck.
    const wait = first.current || page > 1 ? 0 : TYPING_PAUSE
    first.current = false

    const timer = setTimeout(async () => {
      if (page > 1) setLoadingMore(true)
      else setLoading(true)
      setError(null)
      try {
        const antwort = await fetcher({ ...params, page }, controller.signal)
        // ERSETZEN bei Seite 1, ANHAENGEN danach. Seite 1 kommt auch nach
        // einem Filterwechsel, und dort waere Anhaengen falsch.
        setRows((bisher) => page === 1
          ? antwort.items
          : [...bisher, ...antwort.items])
        setHasMore(antwort.has_more)
        setTotal(antwort.total)
        setDenied(false)
      } catch (cause) {
        // Ein Abbruch ist kein Fehler: Er kam, weil schon die naechste
        // Abfrage laeuft.
        if (cause instanceof Aborted) return
        const deutung = deuteFehler(cause)
        setDenied(deutung.denied)
        setError(deutung.message)
        // NUR die erste Seite raeumt auf. Scheitert eine Nachladung, bleibt
        // das Gelesene stehen — es ist nicht falsch geworden.
        if (page === 1) setRows([])
        setHasMore(false)
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
          setLoadingMore(false)
        }
      }
    }, wait)

    return () => { clearTimeout(timer); controller.abort() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, page, enabled, tick])

  // Von vorn, mit dem, was gerade gilt. NACH EINEM SCHREIBEN, nicht live:
  // Der Aufrufer weiss, dass sich etwas geaendert hat, und will die Liste
  // dazu sehen — Seite 1 reicht, die angehaengten Seiten kommen beim
  // Scrollen wieder. Ohne Tipppause: Hier hat niemand getippt.
  const reload = useCallback(() => {
    first.current = true
    setPage(1)
    setTick((n) => n + 1)
  }, [])

  const loadMore = useCallback(() => {
    // Die Wache liegt HIER und nicht beim Aufrufer: Der Beobachter am Ende
    // der Tabelle meldet mehrfach, waehrend die Ladung laeuft — ohne sie
    // liefen drei Abfragen fuer eine Seite.
    setPage((jetzt) => (hasMore && !loading && !loadingMore ? jetzt + 1 : jetzt))
  }, [hasMore, loading, loadingMore])

  return { rows, loading, loadingMore, error, denied, hasMore, loadMore, total,
           reload }
}
